import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import {
    CalendarToday,
    DateRange,
    AccountCircle,
} from '@mui/icons-material';

interface StatisticsCardsProps {
    todayProduction: number;
    monthlyProduction: number;
    topUser: { username: string; totalProductions: number } | null;
}

export const StatisticsCards: FC<StatisticsCardsProps> = ({
    todayProduction,
    monthlyProduction,
    topUser,
}) => {
    const { t } = useTranslation();

    const cards = [
        {
            title: t('aiStudioAnalytics.todayProduction'),
            value: todayProduction,
            icon: <CalendarToday sx={{ fontSize: 24, color: '#0f172a' }} />,
            bgColor: '#ffffff',
            borderColor: '#e2e8f0',
            textColor: '#0f172a',
            iconBgColor: '#f1f5f9',
        },
        {
            title: t('aiStudioAnalytics.monthlyProduction'),
            value: monthlyProduction,
            icon: <DateRange sx={{ fontSize: 24, color: '#0f172a' }} />,
            bgColor: '#ffffff',
            borderColor: '#e2e8f0',
            textColor: '#0f172a',
            iconBgColor: '#f1f5f9',
        },
        {
            title: t('aiStudioAnalytics.topUser'),
            value: topUser ? `${topUser.username} (${topUser.totalProductions})` : '-',
            icon: <AccountCircle sx={{ fontSize: 24, color: '#0f172a' }} />,
            bgColor: '#ffffff',
            borderColor: '#e2e8f0',
            textColor: '#0f172a',
            iconBgColor: '#f1f5f9',
        },
    ];

    return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {cards.map((card, index) => (
                <Grid
                    key={index}
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 4
                    }}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            backgroundColor: card.bgColor,
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: card.borderColor,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
                                borderColor: 'primary.light',
                            },
                        }}
                    >
                        <CardContent sx={{ p: '24px !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: 'text.secondary', 
                                            fontWeight: 600, 
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            display: 'block',
                                            mb: 1
                                        }}
                                    >
                                        {card.title}
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        component="div"
                                        sx={{ 
                                            fontWeight: 700, 
                                            color: card.textColor,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {card.value}
                                    </Typography>
                                </Box>
                                <Box 
                                    sx={{ 
                                        width: 48, 
                                        height: 48, 
                                        borderRadius: '10px', 
                                        backgroundColor: card.iconBgColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        ml: 2
                                    }}
                                >
                                    {card.icon}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

